import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../../../../components/ui';
import { TimesheetEntry } from '../hooks/useTimesheets';
import { Person } from '../hooks/usePeople';
import { Milestone, TransformedProject } from '../hooks/useProjects';

interface TimesheetFormData {
  projectId: string;
  employeeId: string;
  dateWorked: string;
  hours: string;
  notes: string;
  billable: boolean;
  milestoneId: string;
}

interface FormErrors {
  projectId?: string;
  employeeId?: string;
  dateWorked?: string;
  hours?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  timesheet?: TimesheetEntry | null;
  employees?: Person[];
  milestones?: Milestone[];
  loading?: boolean;
  selectedEmployeeId?: string | null;
  projects?: TransformedProject[];
  selectedProjectId?: string | null;
}

export const TimesheetModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  timesheet = null,
  employees = [],
  milestones = [],
  loading = false,
  selectedEmployeeId = null,
  projects = [],
  selectedProjectId = null,
}) => {
  const [formData, setFormData] = useState<TimesheetFormData>({
    projectId: '',
    employeeId: '',
    dateWorked: '',
    hours: '',
    notes: '',
    billable: true,
    milestoneId: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const availableMilestones = React.useMemo(() => {
    if (!formData.projectId && (milestones?.length ?? 0) > 0) {
      return milestones;
    }

    if (formData.projectId && (projects?.length ?? 0) > 0) {
      const project = projects.find(p => p.id === formData.projectId);
      return project?.milestones ?? [];
    }

    return [];
  }, [formData.projectId, projects, milestones]);

  useEffect(() => {
    if (timesheet) {
      setFormData({
        projectId: timesheet.projectId ?? timesheet.project_id ?? selectedProjectId ?? '',
        employeeId: timesheet.employeeId ?? timesheet.employee_id ?? '',
        dateWorked: timesheet.dateWorked ?? timesheet.date_worked ?? '',
        hours: String(timesheet.hours ?? ''),
        notes: timesheet.notes ?? '',
        billable: timesheet.billable ?? true,
        milestoneId: timesheet.milestoneId ?? timesheet.milestone_id ?? ''
      });
    } else {
      setFormData({
        projectId: selectedProjectId ?? '',
        employeeId: selectedEmployeeId ?? '',
        dateWorked: new Date().toISOString().split('T')[0],
        hours: '',
        notes: '',
        billable: true,
        milestoneId: ''
      });
    }
    setErrors({});
  }, [timesheet, isOpen, selectedEmployeeId, selectedProjectId]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if ((projects?.length ?? 0) > 0 && !formData.projectId) {
      newErrors.projectId = 'Project is required';
    }
    if (!formData.employeeId) {
      newErrors.employeeId = 'Employee is required';
    }
    if (!formData.dateWorked) {
      newErrors.dateWorked = 'Date is required';
    }
    if (!formData.hours || Number.parseFloat(formData.hours) <= 0) {
      newErrors.hours = 'Hours must be greater than 0';
    }
    if (Number.parseFloat(formData.hours) > 24) {
      newErrors.hours = 'Hours cannot exceed 24';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: Record<string, unknown> = {
      employee_id: formData.employeeId,
      date_worked: formData.dateWorked,
      hours: Number.parseFloat(formData.hours),
      notes: formData.notes || null,
      billable: formData.billable,
      milestone_id: formData.milestoneId || null
    };

    if (formData.projectId) {
      submitData.projectId = formData.projectId;
    }

    await onSubmit(submitData);
  };

  const handleChange = (field: keyof TimesheetFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  let submitButtonLabel: string;
  if (loading) {
    submitButtonLabel = 'Saving...';
  } else if (timesheet) {
    submitButtonLabel = 'Update Entry';
  } else {
    submitButtonLabel = 'Add Entry';
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={timesheet ? 'Edit Timesheet Entry' : 'Add Timesheet Entry'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {(projects?.length ?? 0) > 0 && (
          <div>
            <label htmlFor="project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project *
            </label>
            <select id="project"
              value={formData.projectId}
              onChange={(e) => handleChange('projectId', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.projectId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              disabled={loading}
            >
              <option value="">Select a project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {errors.projectId && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.projectId}</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="employee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Employee *
          </label>
          <select id="employee"
            value={formData.employeeId}
            onChange={(e) => handleChange('employeeId', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              errors.employeeId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            disabled={loading}
          >
            <option value="">Select an employee</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.name} {emp.email && `(${emp.email})`}
              </option>
            ))}
          </select>
          {errors.employeeId && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.employeeId}</p>
          )}
        </div>

        <div>
          <label htmlFor="ts-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date *
          </label>
          <input
                                                                                  id="ts-date"
            type="date"
            value={formData.dateWorked}
            onChange={(e) => handleChange('dateWorked', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              errors.dateWorked ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            disabled={loading}
          />
          {errors.dateWorked && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.dateWorked}</p>
          )}
        </div>

        <div>
          <label htmlFor="hours" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Hours *
          </label>
          <input id="hours"
            type="number"
            step="0.25"
            min="0"
            max="24"
            value={formData.hours}
            onChange={(e) => handleChange('hours', e.target.value)}
            placeholder="e.g., 8 or 7.5"
            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              errors.hours ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            disabled={loading}
          />
          {errors.hours && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.hours}</p>
          )}
        </div>

        {(availableMilestones?.length ?? 0) > 0 && (
          <div>
            <label htmlFor="milestone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Milestone (Optional)
            </label>
            <select id="milestone"
              value={formData.milestoneId}
              onChange={(e) => handleChange('milestoneId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={loading}
            >
              <option value="">No milestone</option>
              {availableMilestones.map(milestone => (
                <option key={milestone.id} value={milestone.id}>
                  {milestone.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.billable}
              onChange={(e) => handleChange('billable', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              disabled={loading}
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Billable
            </span>
          </label>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes (Optional)
          </label>
          <textarea id="notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            placeholder="Add any notes about this time entry..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            disabled={loading}
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {submitButtonLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default TimesheetModal;
